/*----------------------------------
- AGENDA PAGE CONTENT CATALOG (SSOT)
----------------------------------*/

/**
 * @docs docs/features/agenda
 * @rule Copy states what the agenda holds (TLD launch phases, root zone changes, industry
 *       events) and what a member adds in Product, never a figure the page cannot read from
 *       the backend, never where any data comes from beyond the GeoNames credit the map owes.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { AGENDA_BOARD_DEFAULT_COPY, type AgendaBoardCopy } from '@uniquedomains/components/agenda/board/boardCopy';
import { NEWSLETTER_AGENDA_DAYS_AHEAD } from '@uniquedomains/components/contracts/newsletter/public';

/*----------------------------------
- NEWSLETTER BAND
----------------------------------*/

/**
 * The newsletter band of the agenda and of the three TLD pages (`NewsletterInkBand`), and the
 * phone action that scrolls to it. The form subscribes to the whole newsletter, so the body names
 * the other two weekly sends too: if the band ever subscribes to the agenda alone, its second
 * sentence is the one string that changes.
 */
export const AGENDA_NEWSLETTER_BAND_CONTENT = {
    heading: 'The agenda by email, every Wednesday',
    body: `TLD launch phases, registry changes and industry events for the next ${NEWSLETTER_AGENDA_DAYS_AHEAD} days. You also get the news brief on Monday and one-word opportunities on Thursday. Free, one click to leave.`,
    mobileAction: 'Get the Wednesday agenda',
} as const;

/*----------------------------------
- CONTENT
----------------------------------*/

const AGENDA_LEDE = 'New TLD launch phases, root zone changes and domain industry events, by date.';

export const AGENDA_PAGE_CONTENT = {
    meta: {
        pageTitle: 'Domaining Agenda: TLD launch dates, registry changes and industry events',
        metaDescription:
            'New TLD launch phases, root zone changes and domain industry events, month by month on a calendar, a map and a list by day. Add it to your calendar app with a free feed.',
        /** The `text/calendar` alternate link's title: the feed a calendar app finds in the head. */
        calendarFeedTitle: 'Unique Domains agenda calendar feed',
        markdownTitle: 'Markdown version',
    },
    /** The board's words: the shared defaults with the page's own title (the one `h1`) and lede. */
    board: { ...AGENDA_BOARD_DEFAULT_COPY, title: 'Domaining Agenda', lede: AGENDA_LEDE } satisfies AgendaBoardCopy,
    feeds: {
        /** The title bar's popover pill. */
        summary: 'Add to calendar',
        heading: 'Calendar feeds',
        // A click on an https feed downloads a one-time copy in most browsers: the copy names the
        // subscribe-by-link step, which is what keeps new dates arriving.
        body: 'Add a feed by its link in Google Calendar, Apple Calendar or Outlook. New dates appear on their own.',
        allLabel: 'Everything',
        copy: 'Copy link',
        copied: 'Copied',
        copyLabel: (feed: string) => `Copy the ${feed} feed link`,
    },
    myDates: {
        eyebrow: 'Your dates',
        heading: 'See your own dates next to these',
        body: 'Your calendar in Unique Domains adds your watchlist expiries and Radar dates to this agenda, with a private calendar feed of its own.',
        ctaLabel: 'Get my dates',
    },
    relatedLinksTitle: 'Continue with Unique Domains',
    unavailable: 'The agenda is temporarily unavailable. Please try again shortly.',
    mobileActions: {
        /** The phone bar's first action, opening `/agenda.ics`: the design's one word, so the second action fits unclipped. */
        feed: 'Feed',
    },
} as const;
